import { Card, CardHeader, CardBody, UncontrolledTooltip } from "reactstrap";
import { format } from "date-fns";
import cronstrue from "cronstrue";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faXmark,
    faPause,
    faPlay,
    faPenToSquare,
    faTrashCan,
} from "@fortawesome/free-solid-svg-icons";

import {
    ISchedule,
    ModalType,
    PAUSED_FLAG,
} from "Pages/Setup/Scheduler/Scheduler";
import styles from "Pages/Setup/Scheduler/Components/Schedule/Schedule.module.css";
import useHelpers from "Hooks/useHelpers";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import {
    formatScheduleIntegration,
    splitScheduleIntegration,
} from "Pages/Setup/Scheduler/scheduleArgs";

interface IProps {
    getSchedules: () => void;
    integrations: IIntegration[];
    schedule: ISchedule;
    onSetActiveModal: (modal: ModalType) => void;
    onSetScheduleClicked: (schedule: ISchedule) => void;
}

export default function Schedule({
    getSchedules,
    integrations,
    schedule,
    onSetActiveModal,
    onSetScheduleClicked,
}: IProps) {
    const { canEditSchedule } = useHelpers();
    const { post, put } = useFetch();
    const { newAlert } = useAlertContext();

    /** Handles edit/delete button clicks. Besides setting the active modal state in the parent
     * to open the correct modal, it sets the schedule in the parent so that the modals have
     * access to the correct schedule data.
     */
    const handleClick = (modal: ModalType) => {
        onSetScheduleClicked(schedule);
        onSetActiveModal(modal);
    };

    const runScheduleNow = async () => {
        const response = await post(`/api/schedule/run`, schedule);

        let data;
        try {
            data = await response.json();
        } catch {
            data = response;
        }

        console.log("Run schedule now response: ", data);

        if (response.ok) {
            newAlert({
                message: "Request submitted successfully",
                color: "success",
            });
        } else {
            newAlert({ message: "Request failed", color: "danger" });
        }
    };

    const pauseSchedule = async () => {
        if (!schedule.enabled) {
            newAlert({ message: "Cannot pause a disabled schedule" });
            return;
        }

        const newSchedule = { ...schedule };
        newSchedule.enabled = false;
        newSchedule.description = PAUSED_FLAG + newSchedule.description;

        const url = schedule.trigger.includes(";")
            ? "/api/schedules/pause"
            : "/api/schedule";
        const response = await put(url, newSchedule);
        let data;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Pause schedule response: ", data);
        if (response.ok) {
            getSchedules();
        } else {
            newAlert({ message: "Request failed", color: "danger" });
        }
    };

    const reenableSchedule = async () => {
        if (!schedule.description?.startsWith(PAUSED_FLAG)) {
            newAlert({
                message: "Cannot reenable a schedule that is not paused",
            });
            return;
        }

        const newSchedule = { ...schedule };
        newSchedule.enabled = true;
        newSchedule.description = schedule.description.replace(PAUSED_FLAG, "");

        const url = schedule.trigger.includes(";")
            ? "/api/schedules/pause"
            : "/api/schedule";

        const response = await put(url, newSchedule);
        let data;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log("Reenable schedule response: ", data);
        if (response.ok) {
            getSchedules();
        } else {
            newAlert({ message: "Request failed", color: "danger" });
        }
    };

    const getReadableTrigger = (trigger: string) => {
        const matches = trigger.match(/rate\(([^)]+?)\)/i);

        if (matches) {
            const now = new Date();
            const startDate = new Date(schedule.start_date);

            const startDateText =
                startDate > now
                    ? ` starting at ${format(startDate, " H:mm, EEE, MMM d")}`
                    : ` at ${format(startDate, " H:mm")}`;

            return `Every ${matches[1]}` + startDateText;
        }

        const description = cronstrue.toString(trigger, {
            verbose: true,
            use24HourTimeFormat: true,
        });

        return description;
    };

    const nameShown = schedule.name.replace(/_+/g, " ");

    const isPaused = schedule.description?.startsWith(PAUSED_FLAG);

    return (
        <Card className={styles.schedule}>
            {/* Header with schedule name */}
            <CardHeader className={styles.header}>
                {/* Name, enabled/disabled row */}
                <div className={styles.headerNameAndStatusRow}>
                    <h5 className={styles.title}>{nameShown}</h5>
                    {/* Enabled/Disabled/Paused & icon */}
                    <div className={styles.statusBox}>
                        {isPaused
                            ? "Paused"
                            : schedule.enabled
                            ? "Enabled"
                            : "Disabled"}
                        {isPaused ? (
                            <FontAwesomeIcon
                                icon={faPause}
                                className={styles.paused}
                            />
                        ) : schedule.enabled ? (
                            <FontAwesomeIcon
                                icon={faCheck}
                                className={styles.enabled}
                            />
                        ) : (
                            <FontAwesomeIcon
                                icon={faXmark}
                                className={styles.disabled}
                            />
                        )}
                    </div>
                </div>
                {/* Run/edit/delete button row */}

                <div className={styles.headerRowTwo}>
                    <p className={styles.nextRun}>
                        <b>Next Run: </b>
                        {format(
                            new Date(schedule.nextRun),
                            "EEE, MMM d - HH:mm"
                        )}
                    </p>
                    {canEditSchedule() && (
                        <>
                            <FontAwesomeIcon
                                id={`runNowBtn${schedule.name}`}
                                data-testid="runBtn"
                                icon={faPlay}
                                className={styles.runNowBtn}
                                onClick={runScheduleNow}
                            />
                            <UncontrolledTooltip
                                placement="top"
                                target={`runNowBtn${schedule.name}`}
                            >
                                Run Now
                            </UncontrolledTooltip>

                            <FontAwesomeIcon
                                id={`pauseBtn${schedule.name}`}
                                data-testid="pauseBtn"
                                icon={faPause}
                                className={styles.pauseBtn}
                                onClick={
                                    isPaused ? reenableSchedule : pauseSchedule
                                }
                            />
                            <UncontrolledTooltip
                                placement="top"
                                target={`pauseBtn${schedule.name}`}
                            >
                                {isPaused
                                    ? "Reenable Schedule"
                                    : "Pause Schedule"}
                            </UncontrolledTooltip>

                            <FontAwesomeIcon
                                id={`editBtn${schedule.name}`}
                                data-testid="editBtn"
                                icon={faPenToSquare}
                                className={styles.editBtn}
                                onClick={() => handleClick("edit")}
                            />
                            <UncontrolledTooltip
                                placement="top"
                                target={`editBtn${schedule.name}`}
                            >
                                Edit
                            </UncontrolledTooltip>

                            <FontAwesomeIcon
                                id={`deleteBtn${schedule.name}`}
                                data-testid="deleteBtn"
                                icon={faTrashCan}
                                className={styles.deleteBtn}
                                onClick={() => handleClick("delete")}
                            />
                            <UncontrolledTooltip
                                placement="top"
                                target={`deleteBtn${schedule.name}`}
                            >
                                Delete
                            </UncontrolledTooltip>
                        </>
                    )}
                </div>
            </CardHeader>

            {/* Body with schedule details */}
            <CardBody className={styles.cardBody}>
                <div className={styles.trigger}>
                    <b>Trigger: </b>
                    <i>
                        {!schedule.trigger.includes(";") ? (
                            <span>{getReadableTrigger(schedule.trigger)}</span>
                        ) : (
                            <ul>
                                {schedule.trigger.split(";").map((t) => (
                                    <li key={t}>{getReadableTrigger(t)}</li>
                                ))}
                            </ul>
                        )}
                    </i>
                </div>
                <div>
                    <b>Integrations</b>{" "}
                    <ul className={styles.integrations}>
                        {schedule.integrations.map((integration, index) => {
                            const { process } =
                                splitScheduleIntegration(integration);
                            const isStale = process
                                && process !== "Select Integration"
                                && !integrations.some((int) => int.process === process);
                            return (
                                <li key={integration + index}>
                                    {formatScheduleIntegration(integration)}
                                    {isStale && (
                                        <span style={{ color: "red" }}> *Delete - No longer exists</span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </CardBody>
        </Card>
    );
}
